/**
 * Estrae un messaggio d'errore leggibile dal body di una risposta API fallita.
 *
 * Il gateway (alveare) non ha un formato d'errore unico; forme osservate in produzione:
 *   {code, message: {text, locale}}          — errore applicativo del monolite inoltrato
 *   {code, message: "Problemi tecnici"}      — errore tecnico
 *   {statusCode, message: "<json>", error}   — BadRequestException con stringa JSON
 *   {entity, error: {code, message: {text}}} — body monolite verbatim
 *   {response: {message: {text}}}            — HttpException serializzata
 *   {statusCode, message: ["a", "b"], error} — validazione class-validator (es. /auth/register)
 *   {}                                       — eccezione non serializzabile
 *
 * Ritorna sempre una stringa non vuota oppure null: mai un oggetto,
 * così il template non può renderizzare "[object Object]" o JSON grezzo.
 *
 * @param {unknown} errorData - Body (già parsato o stringa) della risposta d'errore
 * @returns {string|null}
 */
export function extractApiErrorMessage(errorData) {
	if (errorData == null) return null;

	if (typeof errorData === 'string') {
		const parsed = tryParseJson(errorData);
		if (parsed != null) return extractApiErrorMessage(parsed);
		const trimmed = errorData.trim();
		return trimmed !== '' ? trimmed : null;
	}

	if (typeof errorData !== 'object') return null;

	const candidates = [errorData.message, errorData.error?.message, errorData.response?.message];

	for (const candidate of candidates) {
		if (typeof candidate === 'string') {
			const parsed = tryParseJson(candidate);
			if (parsed != null) {
				const nested = extractApiErrorMessage(parsed);
				if (nested) return nested;
			}
			const trimmed = candidate.trim();
			if (trimmed !== '') return trimmed;
		} else if (Array.isArray(candidate)) {
			const joined = candidate
				.filter((item) => typeof item === 'string')
				.map((item) => item.trim())
				.filter((item) => item !== '')
				.join('; ');
			if (joined !== '') return joined;
		} else if (candidate && typeof candidate === 'object' && typeof candidate.text === 'string') {
			const trimmed = candidate.text.trim();
			if (trimmed !== '') return trimmed;
		}
	}

	return null;
}

/**
 * @param {string} text
 * @returns {object|null} L'oggetto parsato, o null se non è JSON di un oggetto
 */
function tryParseJson(text) {
	const trimmed = text.trim();
	if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
	try {
		const parsed = JSON.parse(trimmed);
		return typeof parsed === 'object' ? parsed : null;
	} catch {
		return null;
	}
}
